/**
 * Page de liste des séjours avec modal d'inscription.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { sejourService, enfantService, inscriptionService } from '../services/api';
import { CalendarIcon, MapPinIcon, UsersIcon, CurrencyEuroIcon, PlusIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';
import { confirm } from '../components/ConfirmContainer';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';
import logger from '../utils/logger';

const SejoursList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const [sejours, setSejours] = useState([]);
  const [enfants, setEnfants] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);
  const [navbarContent, setNavbarContent] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedSejour, setSelectedSejour] = useState(null);
  const [selectedEnfant, setSelectedEnfant] = useState('');
  const [selectedTranche, setSelectedTranche] = useState('');
  const [viaPartenaire, setViaPartenaire] = useState(false);
  const [selectedPartenaire, setSelectedPartenaire] = useState('');

  // États spécifiques à la recherche admin (autocomplete)
  const [adminSearch, setAdminSearch] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState([]);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);
  const [adminSearchEnfant, setAdminSearchEnfant] = useState(null);
  const searchCounterRef = useRef(0);

  useEffect(() => {
    loadData();
    if (!user) {
      loadNavbar();
    }
  }, [user]);

  const loadNavbar = async () => {
    try {
      const response = await sejourService.get('/custom-pages/get_navbar/');
      setNavbarContent(response.data);
    } catch (err) {
      logger.error('Erreur lors du chargement de la navbar:', err);
    }
  };

  useEffect(() => {
    // Ouvrir automatiquement le modal si l'URL contient le paramètre openModal ou inscriptionSejourId
    const modalSejourId = searchParams.get('openModal') || searchParams.get('inscriptionSejourId');
    if (modalSejourId && sejours.length > 0) {
      const sejour = sejours.find(s => s.id === parseInt(modalSejourId));
      if (sejour) {
        handleOpenModal(sejour);
        // Retirer le paramètre de l'URL
        searchParams.delete('openModal');
        searchParams.delete('inscriptionSejourId');
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, sejours]);

  useEffect(() => {
    // Exécuter le JavaScript de la navbar si non connecté
    if (!user && navbarContent?.navbar_js) {
      try {
        const navbarScript = document.createElement('script');
        navbarScript.textContent = navbarContent.navbar_js;
        document.body.appendChild(navbarScript);

        return () => {
          if (navbarScript.parentNode) {
            document.body.removeChild(navbarScript);
          }
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript de la navbar:', err);
      }
    }
  }, [user, navbarContent]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger les séjours (toujours accessible)
      const sejoursRes = await sejourService.getAll();
      const sejoursData = sejoursRes.data.results || sejoursRes.data;
      setSejours(Array.isArray(sejoursData) ? sejoursData : []);

      // Les admins utilisent la recherche à la demande — pas de chargement initial
      if (user && !isAdmin) {
        const [enfantsRes, inscriptionsRes] = await Promise.all([
          enfantService.getAll(),
          inscriptionService.getAll(),
        ]);
        const enfantsData = enfantsRes.data.results || enfantsRes.data;
        setEnfants(Array.isArray(enfantsData) ? enfantsData : []);

        const inscriptionsData = inscriptionsRes.data.results || inscriptionsRes.data;
        setInscriptions(Array.isArray(inscriptionsData) ? inscriptionsData : []);
      }
    } catch (error) {
      logger.error('Erreur lors du chargement:', error);
      // Ne pas afficher d'erreur si non authentifié
      if (user) {
        notify.error('Erreur lors du chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = async (sejour) => {
    // Si non connecté, rediriger vers la page de connexion
    if (!user) {
      notify.info('Veuillez vous connecter pour inscrire un enfant');
      navigate(`/login?next=/sejours?inscriptionSejourId=${sejour.id}`);
      return;
    }

    setSelectedSejour(sejour);

    // Recharger les inscriptions avant le modal (parents uniquement — les admins n'ont pas de check côté client)
    if (!isAdmin) {
      try {
        const inscriptionsRes = await inscriptionService.getAll();
        const inscriptionsData = inscriptionsRes.data.results || inscriptionsRes.data;
        setInscriptions(inscriptionsData);
      } catch (error) {
        logger.error('Erreur lors du rechargement des inscriptions:', error);
      }
    }

    // Ouvrir le modal après avoir mis à jour les inscriptions
    setShowModal(true);
  };

  const handleAjouterEnfant = () => {
    // Rediriger vers la page d'ajout d'enfant avec les paramètres de retour
    navigate(`/enfants?returnTo=sejour&sejourId=${selectedSejour.id}`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSejour(null);
    setSelectedEnfant('');
    setSelectedTranche('');
    setViaPartenaire(false);
    setSelectedPartenaire('');
    setAdminSearch('');
    setAdminSearchResults([]);
    setAdminSearchEnfant(null);
  };

  const handleAdminSearchChange = (e) => {
    const val = e.target.value;
    setAdminSearch(val);
    // Toute modification du champ invalide la sélection courante
    if (adminSearchEnfant) {
      setSelectedEnfant('');
      setAdminSearchEnfant(null);
    }
  };

  const handleAdminSelectEnfant = (enfant) => {
    setSelectedEnfant(String(enfant.id));
    setAdminSearchEnfant(enfant);
    setAdminSearch(`${enfant.prenom} ${enfant.nom}`);
    setAdminSearchResults([]);
  };

  // Recherche autocomplete admin avec debounce (350ms) et protection contre les résultats obsolètes
  useEffect(() => {
    if (!isAdmin || !showModal) return;
    if (adminSearch.length < 2) {
      setAdminSearchResults([]);
      setAdminSearchLoading(false);
      return;
    }
    const currentCounter = ++searchCounterRef.current;
    const timer = setTimeout(async () => {
      setAdminSearchLoading(true);
      try {
        const res = await enfantService.search(adminSearch);
        if (currentCounter === searchCounterRef.current) {
          setAdminSearchResults(res.data.results || res.data);
        }
      } catch (err) {
        if (currentCounter === searchCounterRef.current) {
          logger.error('Erreur recherche enfant admin:', err);
          setAdminSearchResults([]);
        }
      } finally {
        if (currentCounter === searchCounterRef.current) {
          setAdminSearchLoading(false);
        }
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [adminSearch, isAdmin, showModal]);

  const handleSubmitInscription = async (e) => {
    e.preventDefault();

    if (!selectedEnfant) {
      notify.warning('Veuillez sélectionner un enfant');
      return;
    }

    // Si le séjour a des tranches de prix, vérifier qu'une tranche est sélectionnée
    if (selectedSejour.has_tranches_prix && selectedSejour.tranches_prix?.length > 0 && !selectedTranche) {
      notify.warning('Veuillez sélectionner une tranche de prix');
      return;
    }

    // Vérifier qu'un partenaire est sélectionné si via partenaire
    if (viaPartenaire && !selectedPartenaire) {
      notify.warning('Veuillez sélectionner un partenaire');
      return;
    }

    try {
      const inscriptionData = {
        enfant: parseInt(selectedEnfant),
        sejour: selectedSejour.id,
        via_partenaire: viaPartenaire,
      };

      // Si via partenaire, ajouter le partenaire
      if (viaPartenaire && selectedPartenaire) {
        inscriptionData.partenaire = parseInt(selectedPartenaire);
        // Conserver aussi le champ partenaire_affilie pour compatibilité
        const partenaire = selectedSejour.partenaires?.find(p => p.id === parseInt(selectedPartenaire));
        if (partenaire) {
          inscriptionData.partenaire_affilie = partenaire.nom;
        }
      }

      // Si une tranche a été sélectionnée, l'ajouter
      if (selectedTranche) {
        inscriptionData.tranche_prix = parseInt(selectedTranche);
      }

      const response = await inscriptionService.create(inscriptionData);

      notify.success('Inscription créée avec succès !');
      handleCloseModal();

      // Rediriger vers la page de détail de l'inscription
      navigate(`/inscriptions/${response.data.id}`);
    } catch (error) {
      logger.error('Erreur lors de l\'inscription:', error);

      // Gérer le cas d'une inscription existante
      let errorData = error.response?.data;

      // Django REST Framework peut retourner les erreurs sous forme d'arrays
      // Normaliser les données en extrayant les valeurs des arrays si nécessaire
      if (errorData && typeof errorData === 'object') {
        const normalizedData = {};
        for (const [key, value] of Object.entries(errorData)) {
          normalizedData[key] = Array.isArray(value) ? value[0] : value;
        }
        errorData = normalizedData;
      }

      if (errorData?.error === 'inscription_existante') {
        // Recharger les inscriptions pour mettre à jour l'état local (parents uniquement)
        if (!isAdmin) {
          try {
            const inscriptionsRes = await inscriptionService.getAll();
            setInscriptions(inscriptionsRes.data.results || inscriptionsRes.data);
          } catch (refreshError) {
            logger.error('Erreur lors du rechargement des inscriptions:', refreshError);
          }
        }

        const confirmView = await confirm.info(
          `${errorData.message}\n\nVoulez-vous voir l'inscription existante ?`,
          'Inscription existante'
        );

        if (confirmView) {
          handleCloseModal();
          navigate(`/inscriptions/${errorData.inscription_id}`);
        }
      } else {
        // Autres erreurs (places épuisées, validation, etc.)
        let errorMsg;

        // Essayer d'extraire le message d'erreur de différentes sources
        if (typeof errorData === 'string') {
          errorMsg = errorData;
        } else if (Array.isArray(errorData)) {
          errorMsg = errorData[0];
        } else if (errorData?.message) {
          errorMsg = errorData.message;
        } else if (errorData?.non_field_errors && Array.isArray(errorData.non_field_errors)) {
          errorMsg = errorData.non_field_errors[0];
        } else if (errorData?.detail) {
          errorMsg = errorData.detail;
        } else if (typeof errorData === 'object') {
          // Si c'est un objet d'erreurs, prendre le premier message
          const firstKey = Object.keys(errorData)[0];
          const firstError = errorData[firstKey];
          errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
        } else {
          errorMsg = 'Erreur lors de la création de l\'inscription';
        }

        notify.error(errorMsg);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className={user ? "w-full min-h-screen" : "auth-page"}>
      {/* Navbar pour les utilisateurs non connectés */}
      {!user && (
        <>
          {/* Inject navbar CSS from DB if available */}
          {navbarContent?.navbar_css && (
            <style dangerouslySetInnerHTML={{ __html: navbarContent.navbar_css }} />
          )}

          {/* Navbar from DB if available, otherwise use default static navbar */}
          {navbarContent?.navbar_html ? (
            <div dangerouslySetInnerHTML={{ __html: navbarContent.navbar_html }} />
          ) : (
            <nav className="auth-navbar">
              <div className="auth-nav-container">
                <Link to="/" className="auth-nav-logo">
                  <img src="/images/logo_transparent.png" alt="Crions D'Couleurs" className="logo-img" />
                  <span className="logo-text">Crions D'Couleurs</span>
                </Link>

                {/* Desktop Menu */}
                <div className="auth-nav-menu desktop-menu">
                  <Link to="/" className="auth-nav-link">Accueil</Link>
                  <Link to="/#about" className="auth-nav-link">Notre association</Link>
                  <Link to="/#help" className="auth-nav-link">Nous aider</Link>
                  <Link to="/sejours" className="auth-nav-link">Séjours</Link>
                  <Link to="/login" className="auth-nav-btn">Se connecter</Link>
                </div>

                {/* Mobile Menu Button */}
                <button
                  className="mobile-menu-btn"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label="Toggle menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {isMobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>

              {/* Mobile Menu */}
              {isMobileMenuOpen && (
                <div className="mobile-menu">
                  <Link to="/" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                    Accueil
                  </Link>
                  <Link to="/#about" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                    Notre association
                  </Link>
                  <Link to="/#help" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                    Nous aider
                  </Link>
                  <Link to="/sejours" className="mobile-nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                    Séjours
                  </Link>
                  <Link to="/login" className="mobile-nav-btn" onClick={() => setIsMobileMenuOpen(false)}>
                    Se connecter
                  </Link>
                </div>
              )}
            </nav>
          )}
        </>
      )}

      <div className={user ? "max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8" : "max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8"} style={!user ? { paddingTop: '6rem' } : {}}>
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Séjours disponibles</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600">Découvrez nos séjours de vacances et inscrivez vos enfants</p>
        </div>

        {sejours.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-sm sm:text-base text-gray-500">Aucun séjour disponible pour le moment</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {sejours.map((sejour) => (
              <div key={sejour.id} className="bg-white overflow-hidden shadow-lg rounded-lg hover:shadow-xl transition-shadow flex flex-col">
                {sejour.image && (
                  <img
                    src={sejour.image}
                    alt={sejour.titre}
                    className="w-full h-40 sm:h-48 object-cover"
                  />
                )}
                <div className="px-4 sm:px-6 py-4 sm:py-5 flex-1 flex flex-col">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{sejour.titre}</h3>

                  <div className="space-y-2 mt-3 sm:mt-4 flex-1">
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400 flex-shrink-0" />
                      <span className="break-words">{sejour.lieu}</span>
                    </div>

                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400 flex-shrink-0" />
                      <span>Du {new Date(sejour.date_debut).toLocaleDateString('fr-FR')} au{' '}
                        {new Date(sejour.date_fin).toLocaleDateString('fr-FR')}</span>
                    </div>

                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                      <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400 flex-shrink-0" />
                      Âge : {sejour.age_min} - {sejour.age_max} ans
                    </div>

                    <div className="text-xs sm:text-sm text-gray-600">
                      <div className="flex items-start">
                        <CurrencyEuroIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                        {sejour.has_tranches_prix && sejour.tranches_prix?.length > 0 ? (
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Prix selon l'âge :</p>
                            <div className="space-y-1">
                              {sejour.tranches_prix.map((tranche, idx) => (
                                <div key={idx} className="text-xs sm:text-sm">
                                  <span className="font-medium text-gray-700">{tranche.age_min}-{tranche.age_max} ans : </span>
                                  <span className="font-bold text-primary-600">{tranche.prix} €</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <span className="text-base sm:text-lg font-bold text-primary-600">{sejour.prix} €</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {sejour.description && (
                    <p className="mt-3 text-xs sm:text-sm text-gray-500 line-clamp-2">
                      {sejour.description}
                    </p>
                  )}

                  {/* Badge de disponibilité avec infos par tranche d'âge */}
                  <div className="mt-3">
                    {(() => {
                      // Inscriptions pas encore ouvertes
                      if (sejour.inscriptions_ouvertes === false) {
                        return (
                          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border bg-blue-100 text-blue-800 border-blue-200">
                            <span className="mr-1.5">📅</span>
                            <span>Inscriptions bientôt</span>
                          </div>
                        );
                      }
                      // Vérifier si le séjour a des tranches de prix
                      if (sejour.has_tranches_prix && sejour.tranches_prix?.length > 0) {
                        // Vérifier quelles tranches sont complètes
                        const tranchesCompletes = sejour.tranches_prix.filter(t => t.is_complet);
                        const toutesCompletes = tranchesCompletes.length === sejour.tranches_prix.length;

                        if (toutesCompletes) {
                          // Toutes les tranches sont complètes
                          return (
                            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border bg-red-100 text-red-800 border-red-200">
                              <span className="mr-1.5">❌</span>
                              <span>Complet</span>
                            </div>
                          );
                        } else if (tranchesCompletes.length > 0) {
                          // Certaines tranches sont complètes
                          return (
                            <div className="space-y-1">
                              <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border bg-green-100 text-green-800 border-green-200">
                                <span className="mr-1.5">✅</span>
                                <span>Places disponibles</span>
                              </div>
                              <div className="text-xs text-orange-600 mt-1">
                                <span className="font-medium">Complet pour : </span>
                                {tranchesCompletes.map((t, idx) => (
                                  <span key={t.id}>
                                    {t.age_min}-{t.age_max} ans
                                    {idx < tranchesCompletes.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        } else {
                          // Toutes les tranches ont des places
                          return (
                            <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border bg-green-100 text-green-800 border-green-200">
                              <span className="mr-1.5">✅</span>
                              <span>Places disponibles</span>
                            </div>
                          );
                        }
                      } else {
                        // Pas de tranches de prix, affichage simple
                        return (
                          <div className="inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border bg-green-100 text-green-800 border-green-200">
                            <span className="mr-1.5">✅</span>
                            <span>Places disponibles</span>
                          </div>
                        );
                      }
                    })()}
                  </div>

                  {sejour.archive ? (
                    <div className="mt-4 w-full py-2 px-4 rounded-md text-sm sm:text-base font-medium text-center bg-gray-200 text-gray-700 border-2 border-gray-400">
                      📦 Séjour archivé
                    </div>
                  ) : sejour.inscriptions_ouvertes === false ? (
                    <>
                      <div className="mt-4 w-full py-3 px-4 rounded-md text-sm sm:text-base text-center bg-blue-50 text-blue-800 border-2 border-blue-200">
                        📅 Les inscriptions n'ont pas encore débuté
                      </div>
                      {sejour.page_presentation && (
                        <a
                          href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${sejour.id}/presentation/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm sm:text-base font-medium transition-colors bg-white hover:bg-gray-50 active:bg-gray-100 text-primary-600 border-2 border-primary-600"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                          Informations sur le séjour
                        </a>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleOpenModal(sejour)}
                        className="mt-4 w-full py-2 px-4 rounded-md text-sm sm:text-base font-medium transition-colors bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white"
                      >
                        Inscrire un enfant
                      </button>
                      {sejour.page_presentation && (
                        <a
                          href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${sejour.id}/presentation/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm sm:text-base font-medium transition-colors bg-white hover:bg-gray-50 active:bg-gray-100 text-primary-600 border-2 border-primary-600"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                          Informations sur le séjour
                        </a>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal d'inscription */}
        {showModal && selectedSejour && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-start sm:items-center justify-center p-4">
            <div className="relative w-full max-w-md shadow-lg rounded-md bg-white my-4 sm:my-8">
              <div className="flex justify-between items-center mb-4 p-4 sm:p-5 border-b">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">
                  Inscrire un enfant au séjour
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500 -mr-2"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="px-4 sm:px-5">
                <div className="mb-4 p-3 sm:p-4 bg-gray-50 rounded-md">
                  <h4 className="font-medium text-gray-900 text-sm sm:text-base">{selectedSejour.titre}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">{selectedSejour.lieu}</p>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Du {new Date(selectedSejour.date_debut).toLocaleDateString('fr-FR')} au{' '}
                    {new Date(selectedSejour.date_fin).toLocaleDateString('fr-FR')}
                  </p>
                  {selectedSejour.has_tranches_prix && selectedSejour.tranches_prix?.length > 0 ? (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Prix selon l'âge :</p>
                      {selectedSejour.tranches_prix.map((tranche, idx) => (
                        <p key={idx} className="text-xs sm:text-sm">
                          <span className="font-medium">{tranche.age_min}-{tranche.age_max} ans : </span>
                          <span className="font-bold text-primary-600">{tranche.prix} €</span>
                        </p>
                      ))}
                      {selectedEnfant && selectedTranche && (() => {
                        const enfant = isAdmin
                          ? adminSearchEnfant
                          : enfants.find(e => e.id === parseInt(selectedEnfant));
                        const tranche = selectedSejour.tranches_prix.find(t => t.id === parseInt(selectedTranche));
                        if (enfant && tranche) {
                          return (
                            <p className="mt-2 text-base sm:text-lg font-bold text-green-600">
                              Prix pour {enfant.prenom} ({tranche.age_min}-{tranche.age_max} ans) : {tranche.prix} €
                            </p>
                          );
                        }
                      })()}
                    </div>
                  ) : (
                    selectedSejour.prix ? (
                      <p className="text-base sm:text-lg font-bold text-primary-600 mt-2">{selectedSejour.prix} €</p>
                    ) : null
                  )}
                  {selectedSejour.page_presentation && (
                    <a
                      href={`${process.env.REACT_APP_API_URL.replace('/api', '')}/api/sejours/${selectedSejour.id}/presentation/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-colors bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white"
                    >
                      <InformationCircleIcon className="h-4 w-4" />
                      Voir les informations détaillées
                    </a>
                  )}
                </div>

                <form onSubmit={handleSubmitInscription}>
                  {isAdmin ? (
                    // Admin : champ de recherche autocomplete
                    <div className="mb-4">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        Rechercher l'enfant à inscrire *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={adminSearch}
                          onChange={handleAdminSearchChange}
                          onBlur={() => setAdminSearchResults([])}
                          placeholder="Nom ou prénom (min. 2 caractères)"
                          autoComplete="off"
                          className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 pr-8"
                        />
                        {adminSearchLoading && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
                          </div>
                        )}
                        {adminSearchResults.length > 0 && (
                          <ul className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                            {adminSearchResults.map((enfant) => (
                              <li
                                key={enfant.id}
                                onMouseDown={(e) => { e.preventDefault(); handleAdminSelectEnfant(enfant); }}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-primary-50 select-none"
                              >
                                {enfant.prenom} {enfant.nom} ({enfant.age} ans)
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {adminSearch.length > 0 && adminSearch.length < 2 && (
                        <p className="mt-1 text-xs text-gray-400">Entrez au moins 2 caractères pour rechercher</p>
                      )}
                      {adminSearchEnfant && (
                        <p className="mt-1 text-xs text-green-600">
                          ✓ Sélectionné : {adminSearchEnfant.prenom} {adminSearchEnfant.nom} ({adminSearchEnfant.age} ans)
                        </p>
                      )}
                    </div>
                  ) : enfants.length === 0 ? (
                    // Parent sans enfant
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                      <p className="text-xs sm:text-sm text-blue-800 mb-3">
                        Vous n'avez pas encore ajouté d'enfant. Ajoutez-en un pour pouvoir l'inscrire à ce séjour.
                      </p>
                      <button
                        type="button"
                        onClick={handleAjouterEnfant}
                        className="w-full inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
                      >
                        <PlusIcon className="h-5 w-5" />
                        Ajouter un enfant
                      </button>
                    </div>
                  ) : (
                    // Parent avec enfants : liste déroulante
                    <>
                      <div className="mb-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700">
                            Sélectionnez l'enfant à inscrire *
                          </label>
                          <button
                            type="button"
                            onClick={handleAjouterEnfant}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1 self-start sm:self-auto"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Ajouter un enfant
                          </button>
                        </div>
                        <select
                          value={selectedEnfant}
                          onChange={(e) => setSelectedEnfant(e.target.value)}
                          required
                          className="mt-1 block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                        >
                          <option value="">-- Choisir un enfant --</option>
                          {enfants.map((enfant) => {
                            const dejaInscrit = inscriptions.some((insc) => {
                              const inscEnfantId = typeof insc.enfant === 'object' ? insc.enfant.id : insc.enfant;
                              const inscSejourId = typeof insc.sejour === 'object' ? insc.sejour.id : insc.sejour;
                              const estDejaInscrit = inscEnfantId === enfant.id &&
                                inscSejourId === selectedSejour.id &&
                                insc.statut !== 'ANNULEE';
                              if (estDejaInscrit) {
                                logger.log(`Inscription trouvée pour ${enfant.prenom}: ID=${insc.id}, statut=${insc.statut}`);
                              }
                              return estDejaInscrit;
                            });
                            const horsTrancheAge = enfant.age < selectedSejour.age_min || enfant.age > selectedSejour.age_max;
                            return (
                              <option key={enfant.id} value={enfant.id} disabled={dejaInscrit}>
                                {enfant.prenom} {enfant.nom} ({enfant.age} ans)
                                {dejaInscrit ? ' - Déjà inscrit' : ''}
                                {horsTrancheAge && !dejaInscrit ? ' ⚠️ Hors tranche d\'âge' : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    </>
                  )}

                  {(isAdmin || enfants.length > 0) && (
                    <>
                      {selectedSejour.has_tranches_prix && selectedSejour.tranches_prix?.length > 0 && (
                        <div className="mb-4">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                            Sélectionnez la tranche de prix *
                          </label>
                          <select
                            value={selectedTranche}
                            onChange={(e) => setSelectedTranche(e.target.value)}
                            required
                            className="mt-1 block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                          >
                            <option value="">-- Choisir une tranche de prix --</option>
                            {selectedSejour.tranches_prix.map((tranche) => (
                              <option
                                key={tranche.id}
                                value={tranche.id}
                              >
                                {tranche.age_min}-{tranche.age_max} ans : {tranche.prix} €
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="mb-4">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                          Inscription via un partenaire social ?
                        </label>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="partenaire-non"
                              name="viaPartenaire"
                              value="false"
                              checked={!viaPartenaire}
                              onChange={() => setViaPartenaire(false)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                            />
                            <label htmlFor="partenaire-non" className="ml-2 text-xs sm:text-sm text-gray-700">
                              Non
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="partenaire-oui"
                              name="viaPartenaire"
                              value="true"
                              checked={viaPartenaire}
                              onChange={() => setViaPartenaire(true)}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                            />
                            <label htmlFor="partenaire-oui" className="ml-2 text-xs sm:text-sm text-gray-700">
                              Oui
                            </label>
                          </div>
                        </div>
                      </div>

                      {viaPartenaire && (
                        <>
                          <div className="mb-4">
                            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                              Sélectionnez votre partenaire *
                            </label>
                            {selectedSejour?.partenaires && Array.isArray(selectedSejour.partenaires) && selectedSejour.partenaires.length > 0 ? (
                              <select
                                value={selectedPartenaire}
                                onChange={(e) => setSelectedPartenaire(e.target.value)}
                                required
                                className="mt-1 block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                              >
                                <option value="">-- Sélectionnez un partenaire --</option>
                                {selectedSejour.partenaires.map((partenaire) => (
                                  <option key={partenaire.id} value={partenaire.id}>
                                    {partenaire.nom}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-xs sm:text-sm text-red-700">
                                  Aucun partenaire n'est associé à ce séjour. Veuillez contacter l'administration.
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <div className="flex items-start gap-2">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xs sm:text-sm font-medium text-yellow-800">
                                  Important - Places partenaires
                                </h3>
                                <div className="mt-2 text-xs sm:text-sm text-yellow-700">
                                  <p>
                                    Cette inscription doit avoir été effectuée dans le cadre des places pré-réservées
                                    par nos partenaires sociaux. Dans le cas contraire, l'inscription ne sera pas validée
                                    par nos équipes administratives.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {(isAdmin || enfants.length > 0) && (
                    <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 pb-4 sm:pb-5">
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedEnfant}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Créer l'inscription
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SejoursList;
